import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';

/**
 * Custom title strategy that updates the page title on navigation.
 *
 * Supports two route configuration patterns:
 * 1. Direct title property: `{ path: 'users', title: 'Users' }`
 * 2. Data property: `{ path: 'users', data: { title: 'Users' } }`
 *
 * Falls back to application name if no title is configured.
 *
 * @see HIGH-002: Page title not updating on navigation
 */
@Injectable({
  providedIn: 'root'
})
export class PageTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly appName = 'Razor Admin';

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const title = this.buildTitle(snapshot);
    if (title) {
      this.title.setTitle(`${title} | ${this.appName}`);
    } else {
      this.title.setTitle(this.appName);
    }
  }

  /**
   * Build the title from the deepest route snapshot.
   * Checks both the route's title property and data.title.
   */
  override buildTitle(snapshot: RouterStateSnapshot): string | undefined {
    // Traverse to the deepest activated route
    let route = snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }

    // Check for direct title property (Angular 14+ style)
    if (route.title) {
      return route.title;
    }

    // Check for title in route data (legacy pattern used in this codebase)
    if (route.data && route.data['title']) {
      return route.data['title'] as string;
    }

    // Walk back up the tree to find a parent with a title
    let parent = route.parent;
    while (parent) {
      if (parent.title) {
        return parent.title;
      }
      if (parent.data && parent.data['title']) {
        return parent.data['title'] as string;
      }
      parent = parent.parent;
    }

    return undefined;
  }
}
